/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Typography2, breakpoints } from '@coursera/cds-core';

import withNotFound from 'bundles/common/components/withNotFound';
import { getResources } from 'bundles/directory-graphql/common/constants';
import { isValidResource } from 'bundles/directory-graphql/common/utils';
import { ArticlesDirectoryContent } from 'bundles/directory-graphql/components/ArticlesDirectoryContent';
import Collections from 'bundles/directory-graphql/components/Collections';
import Courses from 'bundles/directory-graphql/components/Courses';
import { Degrees, ProfessionalCertificates } from 'bundles/directory-graphql/components/Credentials';
import NotFound from 'bundles/directory-graphql/components/NotFound';
import Queries from 'bundles/directory-graphql/components/Queries';
import Specializations from 'bundles/directory-graphql/components/Specializations';
import Topics from 'bundles/directory-graphql/components/Topics';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';

import _t from 'i18n!nls/directory-graphql';

const styles = {
  directorySection: css`
    padding-top: 20px;
    min-height: 200px;
  `,
  results: css`
    padding-top: 16px;
    padding-bottom: 32px;
    width: 100%;
  `,
  linksContainer: css`
    margin-bottom: 24px;
  `,
  resourceDisplayName: css`
    ${breakpoints.down('xs')} {
      text-align: center;
    }
  `,
  headline: css`
    font-size: 24px;
    line-height: 24px;
    margin-bottom: 10px;
    font-weight: 400;
  `,
  resultsContainer: css`
    padding-top: 20px;
    padding-bottom: 32px;
    position: relative;
    min-height: 1px;
    padding-left: 15px;
    padding-right: 15px;

    @media (min-width: 768px) {
      width: 75%;
      float: left;
    }
  `,
};

const RESOURCES = getResources();

type Props = {
  selectedResource: keyof typeof RESOURCES;
  isDirectoryHomePage: boolean;
};

type ContentProps = {
  selectedResource: keyof typeof RESOURCES;
  isDirectoryHomePage: boolean;
};

export const Content = (props: ContentProps) => {
  const { selectedResource, isDirectoryHomePage } = props;

  switch (selectedResource) {
    case RESOURCES.articles.name:
      return <ArticlesDirectoryContent />;
    case RESOURCES.queries.name:
      return <Queries />;
    case RESOURCES.browse.name:
      return <Topics />;
    case RESOURCES.courses.name:
      return <Courses />;
    case RESOURCES.specializations.name:
      return <Specializations />;
    case RESOURCES.certificates.name:
      return <ProfessionalCertificates />;
    case RESOURCES.collections.name:
      return <Collections />;
    case RESOURCES.degrees.name:
    default:
      return <Degrees isDirectoryHomePage={isDirectoryHomePage} />;
  }
};

export const Results = ({ selectedResource, isDirectoryHomePage }: Props) => {
  const selectedResourceDisplayName = RESOURCES[selectedResource].displayName;

  return (
    <div css={[styles.resultsContainer, styles.directorySection, styles.results]}>
      <CourseraMetatags
        metaNameAndProperties={{
          title: _t('#{selectedResourceDisplayName} Directory', {
            selectedResourceDisplayName,
          }),
        }}
      />
      <Typography2 component="h3" css={[styles.resourceDisplayName, styles.headline]}>
        {selectedResourceDisplayName}
      </Typography2>
      <Content isDirectoryHomePage={isDirectoryHomePage} selectedResource={selectedResource} />
    </div>
  );
};

export default withNotFound({
  test: ({ selectedResource }) => !isValidResource(selectedResource),
  errorComponent: NotFound,
})(Results);
